import { Box, Grid } from '@mui/material';
import Stack from '@mui/material/Stack';
import type React from 'react';
import Toggle from '/@/components/forms/toggle';
import Title from '/@/components/title';
import settings from '/@/core/settings';
import { _, _lazy } from '/@/locale';
import { pickKeys } from '/@/utils';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

interface Pest {
    id: number;
    name: string;
    scientificName?: string;
}

interface ControlPlan {
    id: number;
    cropStage?: { es: string; en: string; pt: string };
    damageLevel?: { es: string; en: string; pt: string };
    pestSize?: { es: string; en: string; pt: string };
    seedTreatment?: { es: string; en: string; pt: string };
    [key: string]: any; // Additional properties
}
interface Configuration {
    key: string;
    value: string;
    label: string;
    configuration?: Configuration[];
    controlPlan?: ControlPlan;
}

type SelectedControlPlans = Record<
    number,
    | {
          cropStage?: string;
          damageLevel?: string;
          pestSize?: string;
          seedTreatment?: string;
          controlPlanId?: number;
          [key: string]: any;
      }
    | undefined
>;

interface PestRowProps {
    pest: Pest;
    configurations: Configuration[];
    selectedControlPlans: SelectedControlPlans;
    handleChange: (selectedControlPlans: SelectedControlPlans) => void;
    defaultControlPlan: ControlPlan;
    recommendation: unknown;
}

const PestRow: React.FC<PestRowProps> = ({
    pest,
    configurations,
    selectedControlPlans,
    defaultControlPlan,
    handleChange,
    recommendation,
}) => {
    const getConfigLabel = (key: string) => {
        return {
            cropStage: _('Estadio del cultivo'),
            damageLevel: _('Nivel de daño'),
            pestSize: _('Tamaño'),
            seedTreatment: _('Tratamiento de semilla'),
        }[key];
    };

    const getLabel = (pest: Pest) => {
        return pest.scientificName ? `${_(pest.name)} / ${pest.scientificName}` : _(pest.name);
    };

    function handleToggle(checked: boolean) {
        if (checked) {
            handleChange({
                ...selectedControlPlans,
                [pest.id]: {
                    cropStage: defaultControlPlan.cropStage?.en,
                    damageLevel: defaultControlPlan.damageLevel?.en,
                    pestSize: defaultControlPlan.pestSize?.en,
                    seedTreatment: defaultControlPlan.seedTreatment?.en,
                    controlPlanId: defaultControlPlan.id,
                },
            });
        } else {
            handleChange({ ...selectedControlPlans, [pest.id]: undefined });
        }
    }

    const handleConfigChange = (
        key: string,
        value: string,
        level: number,
        parentKeys: string[] = [],
        configs: Configuration[]
    ) => {
        const newSelectedControlPlans = { ...selectedControlPlans };
        let controlPlanId;

        // Remove child keys
        const currentConfig = pickKeys(newSelectedControlPlans[pest.id] || {}, ['controlPlanId', ...parentKeys]);

        // Update the control plan ID if needed
        const newSelectedConfig = configs.find((v) => v.value === value && v.key === key);
        if (newSelectedConfig?.controlPlan) {
            controlPlanId = newSelectedConfig.controlPlan.id;
        }
        currentConfig.controlPlanId = controlPlanId;

        // Update Key Value
        currentConfig[key] = value;

        // Update Selected Control Plans
        newSelectedControlPlans[pest.id] = currentConfig;
        handleChange(newSelectedControlPlans);
    };

    const renderConfigurationSelects = (configs: Configuration[], level = 0, parentKeys: string[] = []) => {
        if (!configs) return null;

        const key = configs[0]?.key;

        return (
            <div key={level} className={styles.configurationLevel}>
                <Select
                    color={settings.app.theme}
                    clearable={false}
                    persistPlaceholder
                    placeholder={getConfigLabel(key)}
                    onChange={(value) => handleConfigChange(key, value, level, parentKeys, configs)}
                    value={selectedControlPlans?.[pest.id]?.[key]}
                    options={configs.map((c) => ({
                        label: _(c.label),
                        value: c.value,
                    }))}
                    disabled={recommendation.status === 'read_only'}
                />
                {configs.find((c) => selectedControlPlans?.[pest.id]?.[key] === c.value)?.configuration &&
                    renderConfigurationSelects(
                        configs.find((c) => selectedControlPlans?.[pest.id]?.[key] === c.value)?.configuration!,
                        level + 1,
                        [...parentKeys, key]
                    )}
            </div>
        );
    };

    return (
        <Box
            sx={{
                ':hover': {
                    background: (t) => t.palette.primary.light,
                },
            }}
        >
            <div className={styles.row}>
                <Toggle
                    type='checkbox'
                    theme={settings.app.theme}
                    value={selectedControlPlans?.[pest.id]}
                    label={getLabel(pest)}
                    onChange={(checked: boolean) => handleToggle(checked)}
                    disabled={recommendation.status === 'read_only'}
                />
                <div className={styles.configurations}>
                    <Stack direction={'row'} spacing={2}>
                        {renderConfigurationSelects(configurations)}
                    </Stack>
                </div>
            </div>
        </Box>
    );
};

interface PestChoice {
    pest: {
        id: number;
    };
    defaultControlPlan: {
        id: number;
    };
    configurations: Configuration[];
}

interface PestsConfigurationProps {
    pestChoices: PestChoice[];
    selectedControlPlans: SelectedControlPlans;
    handleChange: (selectedControlPlans: SelectedControlPlans) => void;
    recommendation: unknown;
}

const PestsConfiguration: React.FC<PestsConfigurationProps> = ({
    pestChoices,
    selectedControlPlans,
    handleChange,
    recommendation,
}) => {
    return (
        <Grid container direction='column' sx={{ py: 4, gap: 2 }}>
            <Grid item container direction='column' spacing={2}>
                <Grid item>
                    <Title>{_('Plagas')}</Title>
                </Grid>
                <Grid item>
                    <p>{_('Seleccioná las plagas para las cuales querés obtener recomendación de manejo.')}</p>
                </Grid>
            </Grid>
            <Grid item>
                {pestChoices.map((pestChoice) => {
                    return (
                        <PestRow
                            key={pestChoice.pest.id}
                            {...pestChoice}
                            configurations={pestChoice.configurations}
                            selectedControlPlans={selectedControlPlans}
                            handleChange={handleChange}
                            recommendation={recommendation}
                        />
                    );
                })}
            </Grid>
        </Grid>
    );
};

PestsConfiguration.menuLabel = _lazy('Plagas');

export default PestsConfiguration;
